/**
 * Utility class for tracking performance metrics such as frame rate
 */
export class PerformanceTracker {
  constructor() {
    this.frameRates = [];
    this.frameCount = 0;
    this.lastFpsUpdateTime = performance.now();
    this.currentFps = 0;
    this.averageFps = 0;
    this.updateInterval = 500; // Update FPS every 500ms
  }

  /**
   * Reset performance tracking values
   */
  reset() {
    this.frameRates = [];
    this.frameCount = 0;
    this.lastFpsUpdateTime = performance.now();
    this.currentFps = 0;
    this.averageFps = 0;
  }

  /**
   * Track performance metrics based on frame delta time
   * @param {number} deltaTime - Frame delta time in ms
   */
  update(deltaTime) {
    // Calculate FPS from deltaTime (ms)
    const fps = deltaTime > 0 ? Math.round(1000 / deltaTime) : 0;

    // Increment frame counter
    this.frameCount++;

    // Check if it's time to update FPS measurements
    const now = performance.now();
    const elapsed = now - this.lastFpsUpdateTime;

    if (elapsed >= this.updateInterval) {
      // Calculate current FPS from frame count and elapsed time
      this.currentFps = Math.round((this.frameCount * 1000) / elapsed);

      // Store FPS value in history
      this.frameRates.push(this.currentFps);

      // Keep the array at a reasonable size
      if (this.frameRates.length > 100) {
        this.frameRates.shift();
      }

      // Calculate average FPS
      this.averageFps = Math.round(
        this.frameRates.reduce((a, b) => a + b, 0) / this.frameRates.length
      );

      // Reset counters
      this.frameCount = 0;
      this.lastFpsUpdateTime = now;
    }
  }

  /**
   * Get the current FPS value
   * @returns {number} Current frames per second
   */
  getCurrentFPS() {
    return this.currentFps;
  }

  /**
   * Get the average FPS value
   * @returns {number} Average frames per second
   */
  getAverageFPS() {
    return this.averageFps;
  }

  /**
   * Get all frame rate samples
   * @returns {Array<number>} Array of FPS samples
   */
  getFrameRates() {
    return this.frameRates;
  }

  /**
   * Get performance information including memory usage if available
   * @returns {Object} Performance information
   */
  getPerformanceInfo() {
    const info = {
      currentFps: this.currentFps,
      averageFps: this.averageFps,
    };

    // Add memory info if available
    if (window.performance && window.performance.memory) {
      const memory = window.performance.memory;
      info.memory = {
        usedHeapSize: Math.round(memory.usedJSHeapSize / (1024 * 1024)),
        totalHeapSize: Math.round(memory.totalJSHeapSize / (1024 * 1024)),
        limit: Math.round(memory.jsHeapSizeLimit / (1024 * 1024)),
      };
    }

    return info;
  }
}
